import { Head } from 'vite-react-ssg';
import { Link } from 'react-router-dom';
import { pageTitle } from '@mockd/shared/brand';
import { ROUTES } from '../routes';

const LAST_UPDATED = 'July 3, 2026';

export function Terms() {
    return (
        <div className="min-h-screen bg-base-200">
            <Head>
                <title>{pageTitle('Terms of Service')}</title>
                <meta name="description" content="The terms that govern your use of mockd." />
                <meta property="og:title" content={pageTitle('Terms of Service')} />
                <meta property="og:description" content="The terms that govern your use of mockd." />
                <meta property="og:url" content="https://mockd.sh/terms" />
            </Head>
            <div className="max-w-3xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
                <h1 className="tracking-tight">Terms of Service</h1>
                <p className="mt-2 text-sm text-base-content/60">Last updated: {LAST_UPDATED}</p>

                <div className="mt-10 space-y-8 text-base-content/80 leading-relaxed">
                    <section>
                        <p>
                            These Terms of Service ("Terms") govern your use of mockd, a mock API
                            server platform operated at mockd.sh ("Service"). By creating an account
                            or using the Service, you agree to these Terms. If you don't agree, please
                            don't use the Service.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-base-content mb-2">1. Accounts</h2>
                        <p>
                            You're responsible for maintaining the security of your account credentials
                            and for all activity that occurs under your account. You must provide
                            accurate information when creating an account and keep it up to date.
                            You must be at least 16 years old to use the Service.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-base-content mb-2">2. Acceptable Use</h2>
                        <p>You agree not to use mockd to:</p>
                        <ul className="list-disc list-inside space-y-1 mt-2">
                            <li>Impersonate a real service, brand, or third party in a way intended to deceive (e.g. phishing)</li>
                            <li>Distribute malware, or use mock endpoints to stage or deliver attacks against other systems</li>
                            <li>Attempt to circumvent plan limits, rate limits, or abuse the Service's infrastructure</li>
                            <li>Store or process data you don't have the right to store, including sensitive personal data routed through mock endpoints</li>
                            <li>Interfere with or disrupt the Service, or attempt to access it in unauthorized ways</li>
                            <li>Use the Service for any unlawful purpose</li>
                        </ul>
                        <p className="mt-3">
                            We may suspend or terminate accounts that violate this section, with or
                            without notice, depending on severity.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-base-content mb-2">3. Plans and Billing</h2>
                        <p>
                            mockd offers free and paid plans as described on our{' '}
                            <Link to={ROUTES.pricing()} className="link link-primary">Pricing</Link> page. Paid
                            plans are billed on a recurring basis until canceled. Plan features and
                            limits may change, and we'll make reasonable efforts to notify you of
                            material changes in advance.
                        </p>
                        {/* TODO (launch week, once Stripe is live): add refund policy (e.g. no prorated
                            refunds on cancellation, or a 14-day money-back window — decide), failed-payment
                            handling (grace period before downgrade/suspension), and name Stripe as the
                            payment processor. */}
                    </section>

                    <section>
                        <h2 className="text-base-content mb-2">4. Your Content</h2>
                        <p>
                            You retain ownership of the projects, endpoints, mock configurations, and
                            any data you create or route through the Service ("Your Content"). You
                            grant us a limited license to store, process, and transmit Your Content
                            solely to operate and provide the Service to you.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-base-content mb-2">5. Service Availability</h2>
                        <p>
                            mockd is provided on an "as is" and "as available" basis. We do not
                            guarantee uninterrupted or error-free operation, and we make no service
                            level agreement (SLA) or uptime commitment unless separately agreed in
                            writing. We may modify, suspend, or discontinue any part of the Service at
                            any time.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-base-content mb-2">6. Disclaimers and Limitation of Liability</h2>
                        <p>
                            To the fullest extent permitted by law, mockd disclaims all warranties,
                            express or implied, including warranties of merchantability, fitness for a
                            particular purpose, and non-infringement. We are not liable for any
                            indirect, incidental, special, or consequential damages, or for any loss of
                            data, revenue, or business arising from your use of the Service. Our total
                            liability for any claim relating to the Service is limited to the amount you
                            paid us in the 12 months preceding the claim, or $50 if you're on a free plan.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-base-content mb-2">7. Termination</h2>
                        <p>
                            You may stop using the Service and delete your account at any time. We may
                            suspend or terminate your account if you violate these Terms, fail to pay
                            applicable fees, or if we discontinue the Service. Upon termination, your
                            right to use the Service ends, and we may delete Your Content in accordance
                            with our{' '}
                            <Link to={ROUTES.privacy()} className="link link-primary">Privacy Policy</Link>.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-base-content mb-2">8. Governing Law</h2>
                        <p>
                            These Terms are governed by the laws of the State of Wisconsin, without
                            regard to its conflict-of-law provisions. Any dispute arising out of or
                            relating to these Terms or the Service will be resolved exclusively in the
                            state or federal courts located in Dane County, Wisconsin, and you consent
                            to the jurisdiction of those courts.
                        </p>
                        {/* TODO (launch week): consider adding an indemnification clause (user indemnifies
                            mockd for claims arising from their content/use) and decide whether you want
                            arbitration/class-action waiver language — optional at this scale, common at
                            growth stage. */}
                    </section>

                    <section>
                        <h2 className="text-base-content mb-2">9. Changes to These Terms</h2>
                        <p>
                            We may update these Terms from time to time. If we make material changes,
                            we'll update the "Last updated" date above and, where appropriate, notify
                            you directly. Continued use of the Service after changes take effect
                            constitutes acceptance of the updated Terms.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-base-content mb-2">10. Contact</h2>
                        <p>
                            Questions about these Terms? Email{' '}
                            <a href="mailto:support@mockd.sh" className="link link-primary">support@mockd.sh</a>.
                        </p>
                    </section>
                </div>
            </div>
        </div>
    );
}
